import { atom, useAtomValue } from 'jotai'
import { useCallback } from 'react'
import type { SeatId } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { isUpToGradeSchooler } from '~/lib/seat/seat'
import { seatLabel } from '~/lib/seat/seat2'
import { adultCount$ } from './childMenu'
import { isSeatAvailable } from './stateMachine/assign'
import { childCount$, seatId$, useChildCount, visitorsCount$ } from './stateMachine/context'
import type { ReserveInputSeat } from './stateMachine/state'
import { seats$, useTransition } from './stateMachine/state'
import type { DisplaySeat } from './stateMachine/statemachine'

const displaySeats$ = atom((get) => {
  const seats = get(seats$)
  const visitorsCount = get(visitorsCount$)
  const adultCount = get(adultCount$)
  const childCount = get(childCount$)
  return seats.map((seat) => createDisplaySeat(seat, visitorsCount, adultCount, childCount))
})
displaySeats$.debugLabel = 'displaySeats$'

export function useDisplaySeats() {
  return useAtomValue(displaySeats$)
}

function createDisplaySeat(
  seat: ReserveInputSeat,
  visitorsCount: number,
  adultCount: number,
  childCount: number
): DisplaySeat {
  return {
    ...seat,
    label: seatLabel(seat),
    disabled: !isSeatAvailable(seat, { childCount }),
    childrenRestriction: createChildrenRestriction(seat, childCount),
    ...createCharge(seat, visitorsCount, adultCount),
  }
}

function createChildrenRestriction(seat: ReserveInputSeat, childCount: number) {
  // 子供がいない場合は何も表示しない
  if (childCount === 0) {
    return
  }
  if (!isUpToGradeSchooler(seat.childUse)) {
    return 'お子様利用不可'
  }
  if (seat.childUse === 'GRADE_SCHOOLERS') {
    return '小学生以上利用可'
  }
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('createChildrenRestrinction', () => {
    it('未就学児以上が利用可能な席', () => {
      const seat = { childUse: 'PRE_SCHOOLERS' } as ReserveInputSeat
      expect(createChildrenRestriction(seat, 0)).toBeUndefined()
      expect(createChildrenRestriction(seat, 1)).toBeUndefined()
    })
    it('小学生以上が利用可能な席', () => {
      const seat = { childUse: 'GRADE_SCHOOLERS' } as ReserveInputSeat
      expect(createChildrenRestriction(seat, 0)).toBeUndefined()
      expect(createChildrenRestriction(seat, 1)).toBe('小学生以上利用可')
    })
    it('中学生以上が利用可能な席', () => {
      const seat = { childUse: 'OVER_MIDDLE_SCHOOLERS' } as ReserveInputSeat
      expect(createChildrenRestriction(seat, 0)).toBeUndefined()
      expect(createChildrenRestriction(seat, 1)).toBe('お子様利用不可')
    })
  })
}

function createCharge(seat: ReserveInputSeat, visitorsCount: number, adultCount: number) {
  switch (seat.charge.__typename) {
    // 人数単位
    case 'SeatChargePerPerson':
      return {
        chargeAmount: seat.charge.price * adultCount,
        chargeDetails: `（${money(seat.charge.price)}円×${adultCount}名）`,
      }
    // テーブル単位
    case 'SeatChargePerTable':
      const tableCount = calcTableCount(visitorsCount, seat.capacity)
      const chargeAmount = seat.charge.price * tableCount
      if (tableCount === 1) {
        return { chargeAmount, chargeDetails: `／卓` }
      }
      return {
        chargeAmount,
        chargeDetails: `（${money(seat.charge.price)}円×${tableCount}卓）`,
      }
    // チャージなし
    case 'SeatChargeNoCharge':
      return { chargeAmount: 0, chargeDetails: '' }
    default:
      throw new Error(
        `Invalid charge type: ${(seat.charge as { __typename: '__invalid__' }).__typename}`
      )
  }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('createCharge', () => {
    const seat = {
      charge: { __typename: 'SeatChargePerPerson', price: 1000 },
      capacity: 4,
    } as ReserveInputSeat

    expect(createCharge(seat, 4, 3)).toStrictEqual({
      chargeAmount: 3000,
      chargeDetails: '（1,000円×3名）',
    })

    seat.charge.__typename = 'SeatChargePerTable'
    expect(createCharge(seat, 4, 3)).toStrictEqual({
      chargeAmount: 1000,
      chargeDetails: '／卓',
    })
    expect(createCharge(seat, 8, 7)).toStrictEqual({
      chargeAmount: 2000,
      chargeDetails: '（1,000円×2卓）',
    })

    seat.charge.__typename = 'SeatChargeNoCharge'
    expect(createCharge(seat, 4, 3)).toStrictEqual({ chargeAmount: 0, chargeDetails: '' })
  })
}

export function calcTableCount(visitorsCount: number, capacity: number) {
  return Math.ceil(visitorsCount / capacity)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('calcTableCount', () => {
    expect(calcTableCount(4, 4)).toBe(1)
    expect(calcTableCount(5, 4)).toBe(2)
  })
}

export const selectedSeat$ = atom((get) => {
  const seatId = get(seatId$)
  if (selectedSeat$ === undefined) {
    return undefined
  }
  return get(displaySeats$).find((seat) => seat.seatId === seatId)
})
selectedSeat$.debugLabel = 'selectedSeat$'

export function useSelectedSeat() {
  return useAtomValue(selectedSeat$)
}

export function useSetSeatId() {
  const send = useTransition()
  const seats = useDisplaySeats()
  const childCount = useChildCount()
  return useCallback(
    (seatId: SeatId) => {
      send({ type: 'updated', seatId, userStatus: { seats, childCount } })
    },
    [childCount, seats, send]
  )
}
